import React, { FunctionComponent, useMemo } from 'react';
import AppLayout, { TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppContent from '@modules/layout/layout/components/AppContent';
import { ReferenceType } from '@modules/client';
import ApiMemberType from '@modules/client/types/APIMemberType';
import { TestId } from '@modules/utilities/constants/testConstants';
import {
  CharacteristicsRowTypes,
  GlobalCharacteristicRowTypes,
  LibraryCharacteristicRowTypes,
  DatatypeCharacteristicRowTypes,
} from './types';
import { getIteratorRows } from './class/components/CharacteristicsIterator';
import { DatatypeCharacteristicsIterator as DatatypeIteratorRows } from './datatype/components/DatatypeCharacteristicIterator';
import { IteratorRow as GlobalOrLibraryIteratorRows } from './globalOrLibrary/components/GlobalOrLibraryCharacteristicsIterator';

export type ApiMemberReferenceProps =
  | {
      referenceType: ReferenceType.CLASS;
      type: ApiMemberType;
      data: CharacteristicsRowTypes;
    }
  | {
      referenceType: ReferenceType.DATATYPE;
      type: ApiMemberType;
      data: DatatypeCharacteristicRowTypes;
    }
  | {
      referenceType: ReferenceType.GLOBAL | ReferenceType.LIBRARY;
      type: ApiMemberType;
      data: GlobalCharacteristicRowTypes | LibraryCharacteristicRowTypes;
    };

type ApiMemberProps = {
  title: string;
  openGraphMeta: Partial<TOpenGraphMeta>;
} & ApiMemberReferenceProps;

// NOTE (tchu, 2025-11-17): You need to set ENABLE_ENGINE_API_MEMBER_PAGES=all in .env.local to see this page locally.
const ApiMember: FunctionComponent<React.PropsWithChildren<ApiMemberProps>> = ({
  referenceType,
  data,
  title,
  openGraphMeta,
}) => {
  const content = useMemo(() => {
    if (referenceType === ReferenceType.CLASS) {
      return getIteratorRows(data, {
        title,
      });
    }
    if (referenceType === ReferenceType.DATATYPE) {
      return <DatatypeIteratorRows rows={[data]} />;
    }
    if (referenceType === ReferenceType.GLOBAL || referenceType === ReferenceType.LIBRARY) {
      return <GlobalOrLibraryIteratorRows row={data} />;
    }
    throw Error(`invalid type: ${referenceType}`);
  }, [data, title, referenceType]);
  return (
    <AppLayout
      testid={TestId.ApiMember}
      pageTitle={title}
      openGraphMeta={openGraphMeta}
      showHrefLang={false}>
      <AppContent>{content}</AppContent>
    </AppLayout>
  );
};

export default ApiMember;
